import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Rate limiting hook to prevent form spam
 * @param maxAttempts - Maximum attempts allowed in the time window
 * @param windowMs - Time window in milliseconds
 * @param blockDurationMs - How long to block after exceeding limit
 */
export function useRateLimit({
  maxAttempts = 3,
  windowMs = 60000, // 1 minute
  blockDurationMs = 120000, // 2 minutes block
} = {}) {
  const [attempts, setAttempts] = useState([]);
  const [blocked, setBlocked] = useState(false);
  const [blockEndTime, setBlockEndTime] = useState(null);
  const [blockTimeRemaining, setBlockTimeRemaining] = useState(0);

  const intervalRef = useRef(null);

  // Update countdown timer
  useEffect(() => {
    if (blocked && blockEndTime) {
      intervalRef.current = setInterval(() => {
        const remaining = Math.max(0, blockEndTime - Date.now());
        setBlockTimeRemaining(Math.ceil(remaining / 1000));

        if (remaining <= 0) {
          setBlocked(false);
          setBlockEndTime(null);
          setAttempts([]);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
        }
      }, 1000);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [blocked, blockEndTime]);

  const checkLimit = useCallback(() => {
    if (blocked) {
      return false;
    }

    const now = Date.now();

    // Filter out old attempts outside the window
    const recentAttempts = attempts.filter(
      (timestamp) => now - timestamp < windowMs
    );

    if (recentAttempts.length >= maxAttempts) {
      // Block the user
      setBlocked(true);
      const endTime = now + blockDurationMs;
      setBlockEndTime(endTime);
      setBlockTimeRemaining(Math.ceil(blockDurationMs / 1000));
      return false;
    }

    // Add new attempt
    setAttempts([...recentAttempts, now]);
    return true;
  }, [attempts, blocked, maxAttempts, windowMs, blockDurationMs]);

  const reset = useCallback(() => {
    setAttempts([]);
    setBlocked(false);
    setBlockEndTime(null);
    setBlockTimeRemaining(0);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  }, []);

  const remainingAttempts = Math.max(
    0,
    maxAttempts - attempts.filter((t) => Date.now() - t < windowMs).length
  );

  return {
    blocked,
    remainingAttempts,
    blockTimeRemaining,
    checkLimit,
    reset,
  };
}

/**
 * Hook to detect and prevent honeypot spam
 * Hidden field that bots might fill out
 */
export function useHoneypot() {
  const [honeypotValue, setHoneypotValue] = useState('');
  const startTime = useRef(Date.now());

  const isBot = useCallback(() => {
    // If honeypot is filled, it's a bot
    if (honeypotValue.trim() !== '') {
      return true;
    }

    // If form was submitted too fast (less than 2 seconds), likely a bot
    const timeTaken = Date.now() - startTime.current;
    if (timeTaken < 2000) {
      return true;
    }

    return false;
  }, [honeypotValue]);

  const reset = useCallback(() => {
    setHoneypotValue('');
    startTime.current = Date.now();
  }, []);

  const HoneypotField = useCallback(
    () => (
      <div
        style={{
          position: 'absolute',
          left: '-9999px',
          top: '-9999px',
          opacity: 0,
          height: 0,
          overflow: 'hidden',
        }}
        aria-hidden="true"
      >
        <label htmlFor="website">Website</label>
        <input
          type="text"
          id="website"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={honeypotValue}
          onChange={(e) => setHoneypotValue(e.target.value)}
        />
      </div>
    ),
    [honeypotValue]
  );

  return {
    isBot,
    HoneypotField,
    reset,
  };
}
